import { formatHours, fromIsoDate, toIsoDate } from '../../lib/format';
import { type PatternDay, scheduledMinutes } from './WeeklyPatternEditor';

/**
 * What a working week is, for the three screens that now read one.
 *
 * This lived inside the person's own panel while that panel was the only place a schedule could be
 * seen. It is shared the moment a roster-wide list exists, because the two screens have to agree
 * about which of several dated rows is the week somebody actually works — a second reading of an
 * effective-dated history is exactly how one screen ends up showing a pattern that was replaced in
 * March while the other shows the current one.
 */

export const SCHEDULES_PATH = '/admin/v1/payroll/schedules';

/**
 * One effective-dated row: the days somebody works from a date, until another row supersedes it.
 *
 * `staffName` arrives only from the roster-wide read. Asking for schedules WITHOUT a staff id
 * answers every row with the person's name on it, which is what lets a list of the whole team be
 * one request rather than one per head.
 */
export interface StaffSchedule {
  id: string;
  staffId: string;
  staffName?: string | null;
  effectiveFrom: string;
  effectiveUntil?: string | null;
  pattern: PatternDay[];
  note?: string | null;
}

/** Sunday first, matching the weekday numbers the pattern and the server's business-day maths use. */
export const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * What a person with no working week costs, said in one place because three screens have to say it.
 *
 * It is not a blank field. Every day of every period comes back flagged, which takes the one-click
 * approval away and asks for a typed reason per person per period; overtime is worked out against
 * a schedule, so with none it computes as zero however long the days ran; and a day nobody was
 * expected to work has no row on the built sheet, so correcting one starts with a date picker.
 */
export const NO_WORKING_WEEK_COSTS =
  'Overtime works out as zero, their hours cannot be approved in one click, and the days they were expected to work never appear on their sheet.';

/** A pattern in weekday order — nothing promises the stored array arrives sorted. */
export function inWeekOrder(pattern: PatternDay[]): PatternDay[] {
  return [...pattern].sort((a, b) => a.weekday - b.weekday);
}

/** Minutes a whole week covers, breaks already taken out, since breaks here are unpaid. */
export function weekMinutes(pattern: PatternDay[]): number {
  return pattern.reduce((sum, day) => sum + scheduledMinutes(day), 0);
}

/** One line summarizing a week, so the common case needs no expanding: "Mon, Tue … · 43h a week". */
export function summarize(pattern: PatternDay[]): string {
  if (!pattern.length) return 'No days set';
  const days = inWeekOrder(pattern);
  return `${days.map((d) => DAY_SHORT[d.weekday]).join(', ')} · ${formatHours(weekMinutes(days))} a week`;
}

/** "10:00–17:00", with the unpaid break said out loud, because it is not part of what is paid. */
export function describeDayTimes(day: PatternDay): string {
  const bite = day.unpaidBreakMinutes ? ` (−${day.unpaidBreakMinutes}m break)` : '';
  return `${day.startTime}–${day.endTime}${bite}`;
}

/**
 * The times inside a week, in the shortest form that stays true.
 *
 * Almost everybody here works one span on every day they are on, and printing that span six times
 * is how a table stops being read at a glance. When the days genuinely differ they are spelled out,
 * because a single span would then be a lie about somebody's Saturday.
 */
export function describeWeekTimes(pattern: PatternDay[]): string[] {
  const days = inWeekOrder(pattern);
  const first = days[0];
  if (!first) return [];
  const alike = days.every(
    (d) =>
      d.startTime === first.startTime &&
      d.endTime === first.endTime &&
      d.unpaidBreakMinutes === first.unpaidBreakMinutes,
  );
  if (alike) return [describeDayTimes(first)];
  return days.map((d) => `${DAY_SHORT[d.weekday]} ${describeDayTimes(d)}`);
}

/**
 * The row in force on a date, out of one person's history newest-first — which is the order the
 * endpoint promises, and the same reading the timesheet build gives it. A row dated ahead is real
 * and worth showing, and it is not the week somebody works today.
 */
export function scheduleInForce(history: StaffSchedule[], today: string): StaffSchedule | undefined {
  const eligible = history.filter(
    (s) => s.effectiveFrom.slice(0, 10) <= today && (!s.effectiveUntil || s.effectiveUntil >= today),
  );
  return eligible.find((s) => s.effectiveUntil) ?? eligible[0];
}

/** The change that lands soonest, if one is dated ahead. Newest-first, so it is the last of them. */
export function scheduleComingUp(history: StaffSchedule[], today: string): StaffSchedule | undefined {
  return history.filter((s) => s.effectiveFrom.slice(0, 10) > today).at(-1);
}

/**
 * Every person's rows, keyed by staff id and still newest-first inside each person.
 *
 * The endpoint orders by effective date across the whole roster, and grouping in one pass keeps
 * that order within each group — so `scheduleInForce` reads a person's history here exactly as it
 * reads the one fetched for a single person.
 */
export function schedulesByStaff(rows: StaffSchedule[]): Map<string, StaffSchedule[]> {
  const byStaff = new Map<string, StaffSchedule[]>();
  for (const row of rows) {
    const held = byStaff.get(row.staffId);
    if (held) held.push(row);
    else byStaff.set(row.staffId, [row]);
  }
  return byStaff;
}

/**
 * When the trading day rolls over, in minutes past midnight. The column's own default: 04:00.
 *
 * The real value is `dayCutoffMinutes` on the location, which is what the timesheet build reads.
 * This fallback is the same one `useBusinessDate` applies, and it is the right answer for this bar
 * until somebody moves the roll-over — at which point a screen passing the location's value is
 * reading the business and a screen relying on this one is reading a guess.
 */
export const DEFAULT_DAY_CUTOFF_MINUTES = 240;

/** 0 = Sunday. Local noon via `fromIsoDate`, so no timezone can shift which weekday a date is. */
function weekdayOfDate(date: string): number | null {
  return fromIsoDate(date)?.getDay() ?? null;
}

/** The calendar date after this one, by local calendar parts on an anchor that is already a date. */
function dayAfter(date: string): string | null {
  const anchor = fromIsoDate(date);
  if (!anchor) return null;
  anchor.setDate(anchor.getDate() + 1);
  return toIsoDate(anchor) ?? null;
}

/**
 * "HH:MM" → minutes past midnight, or null when it cannot be read.
 *
 * Same tolerance as the server's `parseHm`, which is what validated the row on the way in: one or
 * two digits for the hour, and 24:00 is a real time to a schedule.
 */
function startMinutes(time: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 24 || minutes > 59 || (hours === 24 && minutes !== 0)) return null;
  return hours * 60 + minutes;
}

/**
 * The pattern day whose shift belongs to a BUSINESS day — the same reading the timesheet does.
 *
 * A MIRROR of `patternEntryFor` in `backend/src/domains/payroll/routes.ts`, which is where the rule
 * is stated and where any change to it belongs. Kept here rather than restated in the screen that
 * needs it, because two readings of the same effective-dated history are exactly how one screen
 * suggests a shift the sheet it feeds will then report as unscheduled.
 *
 * The rule, in short: `weekday` names the local CALENDAR day a shift starts on, but a timesheet day
 * is a business day and those roll at the location's cutoff. A cleaner scheduled 02:00–06:00 on
 * Wednesday punches in on Wednesday and belongs to TUESDAY's business day. So the entry on this
 * date's weekday is preferred when it starts at or after the cutoff, and the small hours are picked
 * up from the next date's weekday — each arm read against the schedule in force on its OWN calendar
 * date, because the day a replacement takes effect is the day the two arms disagree, and choosing
 * one row by the business day would judge the new schedule's first small-hours shift against the
 * schedule it replaced.
 *
 * `history` is one person's rows, newest-first — the order the endpoint promises and the order
 * `schedulesByStaff` preserves. A row holding no days is an explicit "no fixed schedule" and finds
 * nothing here, which reads the same as having none.
 */
export function patternDayOn(
  history: StaffSchedule[],
  date: string,
  cutoffMinutes: number = DEFAULT_DAY_CUTOFF_MINUTES,
): PatternDay | undefined {
  const weekday = weekdayOfDate(date);
  const tomorrow = dayAfter(date);
  const tomorrowWeekday = tomorrow ? weekdayOfDate(tomorrow) : null;
  if (weekday === null || tomorrow === null || tomorrowWeekday === null) return undefined;
  // An unreadable start time is flagged as an unreadable schedule where the sheet is built; it must
  // not also get to decide which business day it lands on, so it stays on the weekday it is written
  // under. The server's own arm does the same.
  const startsAt = (day: PatternDay) => startMinutes(day.startTime) ?? cutoffMinutes;
  const patternOn = (on: string) => scheduleInForce(history, on)?.pattern ?? [];
  return (
    patternOn(date).find((day) => day.weekday === weekday && startsAt(day) >= cutoffMinutes) ??
    patternOn(tomorrow).find((day) => day.weekday === tomorrowWeekday && startsAt(day) < cutoffMinutes)
  );
}
